/**
 * The contract between the Donor Portal UI and whatever signs donors in.
 *
 * No page, guard or API client in this module talks to Cognito directly -- they
 * all go through this shape, exported from ./index.js. That is deliberate: how
 * donors authenticate is the part of this design most likely to be changed by
 * someone else, and the backend is unaffected by any of it. It only ever verifies
 * a JWT from a configured issuer and scopes queries by its verified email.
 *
 * The shape is a redirect flow. Sign-in happens on a page we do not render (the
 * Cognito Hosted UI), so there is no password anywhere in this contract, and no
 * first-login password change or password reset: the hosted page owns those.
 *
 * ---------------------------------------------------------------------------
 * Expected shape:
 *
 *   configure()
 *     Idempotent setup. Safe to call more than once.
 *
 *   isConfigured(): boolean
 *     False when the environment variables are missing, so the UI can show a
 *     clear message instead of a stack trace.
 *
 *   signIn(returnTo?, { replace? }): Promise<void>
 *     Sends the browser to the provider's sign-in page. In practice it does not
 *     return. returnTo must be confined to the portal, so the round trip can
 *     never become an open redirect. replace: true replaces the current history
 *     entry -- for automatic redirects, so Back cannot trap the donor.
 *
 *   handleRedirectCallback(search): Promise<string>
 *     Run on the callback route with window.location.search. Validates the
 *     response against the sign-in that started in this tab, exchanges it for
 *     tokens, and resolves to the path to send the donor to. Safe to call twice
 *     for the same response. Rejects with { code, message }.
 *
 *   getAccessToken(): Promise<string | null>
 *     Returns a currently-valid token for the API, refreshing it if needed, or
 *     null if there is no usable session. Callers must not cache the result.
 *     (For Cognito this is the ID token -- see donorApi.js.)
 *
 *   getSessionUser(): { email, provider } | null
 *     Synchronous, for first paint. Never a source of authorization -- the
 *     server decides what a donor may see. `provider` names a social sign-in
 *     ("Google"), or is null for a password account.
 *
 *   changePassword(returnTo?): Promise<boolean>
 *     Gets the donor to the provider's own password reset page, returning
 *     through the callback like signIn. The UI never handles a password. May
 *     sign the donor out first (Cognito will not reset a signed-in session);
 *     resolves like signOut when it does.
 *
 *   hasPendingPasswordReset(): boolean
 *   resumePasswordReset(): Promise<boolean>
 *     For the login page, when changePassword went through a sign-out: whether
 *     to carry on to the reset page, and doing so. Single-use; resolves true
 *     when the browser is navigating away.
 *
 *   signOut(): Promise<boolean>
 *     Must revoke the session server-side where the provider supports it, not
 *     merely clear local state, and must not sign the donor out of unrelated
 *     applications that share the identity provider.
 * ---------------------------------------------------------------------------
 */

/** Error codes the UI branches on, normalised across providers. */
export const DonorAuthErrorCode = {
    /** The donor backed out on the provider's page. Not an error to dwell on. */
    CANCELLED: 'CANCELLED',
    /** The callback did not belong to a sign-in from this tab, or could not be redeemed. */
    CALLBACK_INVALID: 'CALLBACK_INVALID',
    NOT_CONFIGURED: 'NOT_CONFIGURED',
    UNKNOWN: 'UNKNOWN',
};
